import { useCallback, useEffect, useState } from 'react';
import { loadUsers } from '../components/UserPicker';

/**
 * Turns an Azure AD user id (oid) into the person's display name, using the
 * same cached user list as UserPicker (one request per page load).
 *
 * `nameFor(oid)` returns the name; the raw id if the person isn't in the list
 * (e.g. they've left); '' for no id, or while the list is still loading.
 */
export function useUserNames(): { nameFor: (oid: string | null | undefined) => string; loaded: boolean } {
  const [names, setNames] = useState<Map<string, string> | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadUsers()
      .then((users) => {
        if (!cancelled) setNames(new Map(users.map((u) => [u.user_id, u.display_name])));
      })
      .catch(() => {
        if (!cancelled) setNames(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const nameFor = useCallback(
    (oid: string | null | undefined) => {
      if (!oid || !names) return '';
      return names.get(oid) || oid;
    },
    [names],
  );

  return { nameFor, loaded: names !== null };
}
